import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { METADATA_TERMS_FIELD } from '@/config/constants';
import type { LoginTripResponse } from '@/features/auth/api/login.api';
import { http } from '@/services/http';
import { retrieveFromUserMetadata } from '@/services/storage/userMetadata';
import type { UserMetadata, UserSubscription } from '@/shared/types/auth';
import { UserAccess } from '@/shared/types/auth';
import { logout } from '@/store/authActions';

export type TripTokenState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled' | 'expired';
  error: string | null;
  userEmail: string | null;
  userAccess: UserAccess | null;
  metadata: UserMetadata[] | null;
  terms: string | null;
  hsObjectId: string | null;
  subscription: UserSubscription | null;
};

const initialState: TripTokenState = {
  status: 'idle',
  error: null,
  userEmail: null,
  userAccess: null,
  hsObjectId: null,
  terms: null,
  metadata: null,
  subscription: null
};

const tripTokenSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTripTokenPending(state) {
      state.status = 'loading';
      state.error = null;
    },
    setTripTokenFulfilled(state, action: PayloadAction<LoginTripResponse>) {
      state.status = 'fulfilled';
      state.userEmail = action.payload.userEmail;
      state.userAccess = UserAccess[action.payload.access as UserAccess];
      state.hsObjectId = action.payload.hsObjectId;
      state.metadata = action.payload.metadata;
      state.terms = retrieveFromUserMetadata(action.payload.metadata, METADATA_TERMS_FIELD);
      state.subscription = action.payload.subscription;
    },
    setTripTokenError(state, action: PayloadAction<unknown>) {
      state.status = 'failed';
      const payload = action.payload as { data?: string; status: number } | undefined;
      state.error = payload?.data ?? 'Login failed';
    },
    acceptTerms(state, action: PayloadAction<string>) {
      const terms = action.payload;
      state.terms = terms;
      saveTerms(terms, state.userEmail ?? '');
    },
    resetStatus(state) {
      state.status = 'idle';
    }
  },
  extraReducers: builder => {
    builder.addCase(logout, () => initialState);
  }
});

const saveTerms = (terms: string, email: string) => {
  http.go.patch(`/users/${email}`, { terms });
};

export const {
  setTripTokenPending,
  setTripTokenFulfilled,
  setTripTokenError,
  acceptTerms,
  resetStatus
} = tripTokenSlice.actions;

export default tripTokenSlice.reducer;
